import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Plane, Calendar, MapPin } from 'lucide-react';

// Helper to parse date without timezone issues
const parseDate = (dateStr) => {
  if (!dateStr) return new Date();
  // Handle both ISO strings and YYYY-MM-DD format
  if (dateStr.includes('T')) {
    return new Date(dateStr);
  }
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export default function TripCountdownWidget({ bookings }) {
  // Filter upcoming trips and sort by date
  const upcomingTrips = bookings
    .filter(b => {
      const tripDate = parseDate(b.tour_date);
      return tripDate > new Date() && (b.status === 'confirmed' || b.status === 'pending_payment');
    })
    .sort((a, b) => parseDate(a.tour_date) - parseDate(b.tour_date))
    .slice(0, 3); // Show max 3 upcoming trips

  if (upcomingTrips.length === 0) {
    return null;
  }

  const calculateCountdown = (tourDate) => {
    const tripDate = parseDate(tourDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    tripDate.setHours(0, 0, 0, 0);
    
    const diffTime = tripDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { days: 0, label: 'Today!' };
    if (diffDays === 1) return { days: 1, label: 'Tomorrow!' };
    if (diffDays < 7) return { days: diffDays, label: 'days away' };
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return { days: diffDays, label: `${weeks} week${weeks > 1 ? 's' : ''} away` };
    }
    const months = Math.floor(diffDays / 30);
    return { days: diffDays, label: `${months} month${months > 1 ? 's' : ''} away` };
  };

  const formatDate = (dateStr) => {
    const date = parseDate(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCountdownColor = (days) => {
    if (days <= 7) return 'from-red-500 to-orange-500';
    if (days <= 30) return 'from-orange-500 to-yellow-500';
    if (days <= 60) return 'from-yellow-500 to-green-500';
    return 'from-green-500 to-cyan-500';
  };

  return (
    <Card className="overflow-hidden border-2 border-secondary/20">
      <div className="bg-gradient-to-r from-secondary to-orange-500 px-4 py-3">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Plane className="h-5 w-5" />
          Trip Countdown
        </h3>
      </div>
      <CardContent className="p-4 space-y-4">
        {upcomingTrips.map((booking, idx) => {
          const countdown = calculateCountdown(booking.tour_date);
          const gradientColor = getCountdownColor(countdown.days);
          
          return (
            <div 
              key={booking.id}
              className={`relative overflow-hidden rounded-xl ${idx === 0 ? 'bg-gradient-to-r ' + gradientColor + ' p-[2px]' : ''}`}
            >
              <div className={`bg-white rounded-xl p-4 ${idx === 0 ? '' : 'border'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-primary text-lg">
                        {booking.trip_title}
                      </h4>
                      {idx === 0 && (
                        <Badge className="bg-secondary text-white text-xs">Next Trip</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(booking.tour_date)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Countdown Display */}
                  <div className="text-right">
                    <div className={`text-3xl font-bold bg-gradient-to-r ${gradientColor} bg-clip-text text-transparent`}>
                      {countdown.days}
                    </div>
                    <div className="text-xs text-muted-foreground font-medium">
                      {countdown.label}
                    </div>
                  </div>
                </div>
                
                {/* Progress bar for first trip */}
                {idx === 0 && countdown.days > 0 && countdown.days <= 90 && (
                  <div className="mt-3">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${gradientColor} transition-all duration-500`}
                        style={{ width: `${Math.max(5, 100 - (countdown.days / 90) * 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 text-center">
                      {countdown.days <= 7 ? "Almost there! Time to pack!" : 
                       countdown.days <= 30 ? "Getting close! Check your documents" :
                       "Plenty of time to prepare"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
